/* eslint-disable react/jsx-curly-newline */
import React, { useCallback, useEffect, useState } from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import InputMask from "react-input-mask";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";

/* region Imports - Pages */
import { IFormConcretingRequestInputs } from "@pages/ConcretingRequest";

/* endregion Imports - Pages */
/* region Imports - GraphQL */
import { useLazyQuery } from "@apollo/client";

import {
  contractConcreteBatchingPlantAllWithPagination,
  TContractConcreteBatchingPlantAllWithPaginationQueries
} from "src/store/graphql/groups/contractConcreteBatchingPlant/query.graphql";
import {
  ConcreteBatchingPlantType,
  ConcretingRequestType,
  ContractConcreteBatchingPlantQueriesGetAllWithPaginationArgs,
  ECommandResultStatus,
  EParameter,
  EWhereClausesOperators,
  ParamQueriesGetAllWithPaginationArgs
} from "@store/graphql/helpers/types.graphql";
import { paramAllWithPagination, TParamAllWithPaginationQueries } from "@graphql/groups/param/query.graphql";

/* endregion Imports - GraphQL */
/* region Imports - Language */
import useTranslation from "@languages/useTranslation";
import { BaseCardMessages } from "src/languages/interfaces";

/* endregion Imports - Language */
/* region Imports - Helpers */
import { getDateLocale, handleErrorMessage } from "@helpers/index";

/* endregion Imports - Helpers */
/* region Imports - Libraries */
import {
  AdapterDateFns,
  DateTimePicker,
  FormHelperText,
  InputLabel,
  LocalizationProvider,
  MenuItem,
  Select,
  TextField
} from "@libraries/mui";

/* endregion Imports - Libraries */
/* region Imports - Styles */
import * as Styled from "./styles";

/* endregion Imports - Styles */

/* region Interfaces */
interface FormConcretingRequestConcretingProps {
  controlForms: UseFormReturn<IFormConcretingRequestInputs>;
  currentConcretingRequest: ConcretingRequestType | undefined;
  onlyView: boolean;
}

interface CustomOptionType {
  id: string;
  value: string;
}

/* endregion Interfaces */

const intervalOptions: CustomOptionType[] = [
  { id: "5", value: "5" },
  { id: "10", value: "10" },
  { id: "15", value: "15" },
  { id: "20", value: "20" },
  { id: "25", value: "25" },
  { id: "30", value: "30" },
  { id: "35", value: "35" },
  { id: "40", value: "40" },
  { id: "45", value: "45" },
  { id: "50", value: "50" },
  { id: "55", value: "55" },
  { id: "60", value: "60" },
  { id: "65", value: "65" },
  { id: "70", value: "70" },
  { id: "75", value: "75" },
  { id: "80", value: "80" },
  { id: "85", value: "85" },
  { id: "90", value: "90" },
  { id: "95", value: "95" },
  { id: "100", value: "100" },
  { id: "105", value: "105" },
  { id: "110", value: "110" },
  { id: "115", value: "115" },
  { id: "120", value: "120" },
  { id: "125", value: "125" },
  { id: "130", value: "130" },
  { id: "135", value: "135" },
  { id: "140", value: "140" },
  { id: "145", value: "145" },
  { id: "150", value: "150" },
  { id: "155", value: "155" },
  { id: "160", value: "160" },
  { id: "165", value: "165" },
  { id: "170", value: "170" },
  { id: "175", value: "175" },
  { id: "180", value: "180" }
];

const FormConcretingRequestConcreting: React.FC<
  FormConcretingRequestConcretingProps
> = ({ controlForms, currentConcretingRequest, onlyView }) => {

  /* region Constants */
  const { t } = useTranslation();
  const dateLocale = getDateLocale();
  const { addToast } = useToast();

  const {
    control,
    setValue,
    getValues,
    formState: { errors: formErrors },
    watch
  } = controlForms;

  // watched values
  // const watchToConfirm = watch("toConfirm");
  const watchContract = watch("contract");

  /* endregion Constants */
  /* region States */

  const [programationSolicitationMinDays, setProgramationSolicitationMinDays] = useState<number>(1);

  // states for autocomplete concrete batching plant
  const [
    openConcreteBatchingPlantAutocomplete,
    setOpenConcreteBatchingPlantAutocomplete
  ] = useState(false);

  const [
    optionsConcreteBatchingPlantAutocomplete,
    setOptionsConcreteBatchingPlantAutocomplete
  ] = useState<ConcreteBatchingPlantType[]>([]);

  /* endregion States */
  /* region GraphQL - Queries */

  // query for contract concrete batching plant
  const [
    getContractConcreteBatchingPlants,
    {
      loading: loadingContractConcreteBatchingPlant,
      error: errorContractConcreteBatchingPlants,
      data: dataContractConcreteBatchingPlants
    }
  ] = useLazyQuery<
    TContractConcreteBatchingPlantAllWithPaginationQueries,
    ContractConcreteBatchingPlantQueriesGetAllWithPaginationArgs
  >(
    contractConcreteBatchingPlantAllWithPagination([
      "id",
      "concreteBatchingPlant.name",
      "concreteBatchingPlant.code",
      "concreteBatchingPlant.id"
    ]),
    {
      fetchPolicy: "network-only"
    }
  );

  // Query for params
  const [getParams] = useLazyQuery<
    TParamAllWithPaginationQueries,
    ParamQueriesGetAllWithPaginationArgs
  >(paramAllWithPagination(["id", "name", "key"]), {
    fetchPolicy: "network-only"
  });

  /* endregion GraphQL - Queries */
  /* region Functions */

  /**
   * Load concrete batching plants
   */
  const loadConcreteBatchingPlants = useCallback(async () => {

    const response = dataContractConcreteBatchingPlants?.contractConcreteBatchingPlant
      .getAllWithPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {

      const concreteBatchingPlants = response.result
        ?.sort((a, b) => {

          if (!a?.priority || !b?.priority) return 0;
          if (a?.priority > b?.priority) return 1;
          if (a?.priority < b?.priority) return -1;

          return 0;
        })
        .map((item) => item?.concreteBatchingPlant);

      const concreteBatchingPlantPrimary = concreteBatchingPlants?.at(0);

      setValue("concreteBatchingPlant", concreteBatchingPlantPrimary);
      setOptionsConcreteBatchingPlantAutocomplete(concreteBatchingPlants as ConcreteBatchingPlantType[]);
    }
  }, [dataContractConcreteBatchingPlants, setValue]);

  /* endregion Functions */

  const loadingConcreteBatchingPlantAutocomplete = openConcreteBatchingPlantAutocomplete
    && optionsConcreteBatchingPlantAutocomplete.length === 0;

  /* region UseEffects */
  useEffect(() => {
    if (watchContract) {
      getContractConcreteBatchingPlants({
        variables: {
          where: [
            {
              property: "contractId",
              operator: EWhereClausesOperators.EQUALS,
              value: watchContract.id
            }
          ]
        }
      });
    }
  }, [watchContract, getContractConcreteBatchingPlants]);

  useEffect(() => {
    /*
     * Get the min days of programation solicitation from database
     */
    const FsetMinDaysOfDb = async () => {
      const { data } = await getParams({
        variables: {
          where: [
            {
              property: "name",
              operator: EWhereClausesOperators.EQUALS,
              value: EParameter.PROG_SOLICITATION_MIN_DAYS
            }
          ]
        }
      });

      if (data?.param.getAllWithPagination.status === "SUCCESS") {
        const result = data?.param.getAllWithPagination.result;

        setProgramationSolicitationMinDays(Number(result?.[0]?.key));
      }
    };

    FsetMinDaysOfDb().then();
  }, [getParams]);

  useEffect(() => {
    if (dataContractConcreteBatchingPlants) loadConcreteBatchingPlants();
    if (errorContractConcreteBatchingPlants) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorContractConcreteBatchingPlants.message
      });
    }
  }, [
    addToast,
    dataContractConcreteBatchingPlants,
    errorContractConcreteBatchingPlants,
    loadConcreteBatchingPlants
  ]);

  useEffect(() => {
    if (!currentConcretingRequest) return;

    setValue("concretingDate", currentConcretingRequest.requestedDate);
    setValue("interval", currentConcretingRequest.interval);
    setValue(
      "observationConcreting",
      currentConcretingRequest.observationConcreting ?? ""
    );
    setValue("requester", currentConcretingRequest.requester);
    setValue(
      "requesterPhoneNumber",
      currentConcretingRequest.requesterPhoneNumber
    );
  }, [currentConcretingRequest, setValue]);

  /* endregion UseEffects */

  return (
    <Styled.FormContainer>
      <Controller
        name="concretingDate"
        control={control}
        defaultValue={null}
        render={({ field: { onChange, value } }) => (
          <LocalizationProvider
            error={!!formErrors.concretingDate}
            dateAdapter={AdapterDateFns}
            locale={dateLocale}
          >
            <DateTimePicker
              value={value}
              label={t(BaseCardMessages.concretingRequestConcretingDateText)}
              onChange={(newDate) => onChange(newDate)}
              minutesStep={15}
              minDateTime={
                new Date(
                  new Date().getFullYear(),
                  new Date().getMonth(),
                  new Date().getDate() + programationSolicitationMinDays,
                  0,
                  0,
                  0,
                  0
                )
              }
              disabled={onlyView}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={!!formErrors.concretingDate}
                  helperText={handleErrorMessage(formErrors.concretingDate, t)}
                  className="default-field"
                  size="small"
                />
              )}
            />
          </LocalizationProvider>
        )}
      />
      <Controller
        name="interval"
        control={control}
        defaultValue={intervalOptions[0].id}
        render={({ field }) => (
          <Styled.CustomFormControl
            error={!!formErrors.interval}
            className="default-field"
            size="small"
            fullWidth
          >
            <InputLabel id="concrete-type-select">
              { `${t(
                BaseCardMessages.concretingRequestConcretingIntervalText
              )} (min)` }
            </InputLabel>
            <Select
              {...field}
              labelId="interval-select"
              label={`${t(
                BaseCardMessages.concretingRequestConcretingIntervalText
              )} (min)`}
              error={!!formErrors.interval}
              // onChange={(e) => handleChange(e.target.value as EConcreteType)}
              disabled={onlyView}
            >
              { intervalOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  <Styled.SelectItem>{ option.value }</Styled.SelectItem>
                </MenuItem>
              )) }
            </Select>
            { !!formErrors.interval && (
              <FormHelperText>
                { handleErrorMessage(formErrors.interval, t) }
              </FormHelperText>
            ) }
          </Styled.CustomFormControl>
        )}
      />
      <Controller
        name="requester"
        control={control}
        defaultValue=""
        render={({ field }) => (
          <TextField
            {...field}
            autoComplete="name"
            label={t(BaseCardMessages.concretingRequestRequesterText)}
            variant="outlined"
            error={!!formErrors.requester}
            helperText={handleErrorMessage(formErrors.requester, t)}
            className="default-field"
            size="small"
            disabled={onlyView}
          />
        )}
      />
      <Controller
        name="requesterPhoneNumber"
        control={control}
        defaultValue=""
        render={({ field }) => (
          <InputMask
            mask="(99) 99999-9999"
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            disabled={onlyView}
          >
            { (inputProps) => (
              <TextField
                {...field}
                {...inputProps}
                autoComplete="phone"
                label={t(
                  BaseCardMessages.concretingRequestRequesterPhoneNumberText
                )}
                type="text"
                variant="outlined"
                error={!!formErrors.requesterPhoneNumber}
                helperText={handleErrorMessage(
                  formErrors.requesterPhoneNumber,
                  t
                )}
                className="default-field"
                size="small"
              />
            ) }
          </InputMask>
        )}
      />
      <Controller
        name="observationConcreting"
        control={control}
        defaultValue=""
        render={({ field }) => (
          <TextField
            {...field}
            autoComplete="off"
            label={t(BaseCardMessages.concretingRequestObservationText)}
            variant="outlined"
            error={!!formErrors.observationConcreting}
            helperText={handleErrorMessage(formErrors.observationConcreting, t)}
            className="default-field"
            size="small"
            disabled={onlyView}
          />
        )}
      />
    </Styled.FormContainer>
  );
};

export default FormConcretingRequestConcreting;
